import { scoreBand, statusOf, TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'

export interface ScoreNumberProps {
  /** 0–10. Null, undefined or 0 with `unscored` reads as not measured. */
  value: number | null | undefined
  /** Treat 0 as "not measured" (an app with no analysis yet). Default true. */
  zeroIsEmpty?: boolean
  /** sm = a table cell, lg = a score card. */
  size?: 'sm' | 'lg'
  /** Show the band's word ("좋음") beside the number. */
  withLabel?: boolean
  /** Competition and entry risk: a high number is bad, so the band is read
   *  from 10 − value. */
  lowerIsBetter?: boolean
  className?: string
}

/** A 0–10 score in its band's colour (≥7 좋음, ≥5 보통, ≥3 낮음, else 나쁨).
 *  The band word is always in the title, so the colour is never alone. */
export function ScoreNumber({ value, zeroIsEmpty = true, size = 'sm', withLabel, lowerIsBetter, className }: ScoreNumberProps) {
  if (value === null || value === undefined || Number.isNaN(value) || (zeroIsEmpty && value === 0)) {
    return (
      <span className={cn('tabular-nums text-muted-foreground', className)} title="점수 없음">
        —
      </span>
    )
  }
  const def = statusOf('score', scoreBand(lowerIsBetter ? 10 - value : value))
  return (
    <span
      title={`${value.toFixed(1)} · ${def.label}`}
      className={cn(
        'inline-flex items-baseline gap-1.5 whitespace-nowrap font-semibold tabular-nums',
        size === 'lg' ? 'text-stat' : 'text-table',
        TONE_CLASS[def.tone].text,
        className,
      )}
    >
      {value.toFixed(1)}
      {withLabel && <span className="text-meta font-medium">{def.label}</span>}
    </span>
  )
}
